import { ChatModelsPayload } from "./chatModel"
import { newRequest, streamRequest } from "./complexRequest"
import { ComplexRequestOption, PageQueryFn, ResponseType, SupportLangsTypeList } from "./type"

// 创建chat
export const createChat = async (
  data?: any,
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<{
    "session_id": number,
    "chat_id": string,
    "robot_id": number
  }>>({
    url: '/client/session/single',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

// 获取最近的聊天
export const getRecentChat:
  PageQueryFn<{
    page_size?: number
    detail?: boolean
  }> = async (
    params,
    option: ComplexRequestOption = {},
    useFetch = true) => {

    return newRequest<any>({
      url: '/client/session/singleList',
      params,
      ...option
    }, useFetch)

  }


// 获取最近的群聊天
export const getRecentRoom:
  PageQueryFn<{
    page_size?: number
    detail?: boolean
  }> = async (
    params,
    option: ComplexRequestOption = {},
    useFetch = true) => {

    return newRequest<any>({
      url: '/client/session/groups',
      params,
      ...option
    }, useFetch)

  }

// 获取聊天信息
export const getChatInfo = async (
  params?: any,
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<any>({
    url: '/client/session/info',
    params,
    method: 'get',
    ...option
  }, useFetch)
}

// 给机器人发送信息
export const sendMessage = async (
  data: {
    session_id: number,
    chat_id: string,
    content: string,
    image_url?: string
    is_next?: boolean
    at_robots?: number[],
    /** 是否流式回复；未传时默认 true。由用户配置 is_chat_stream(0/1) 映射而来 */
    stream?: boolean,
  }, { onData }) => {
  return streamRequest({
    url: '/client/session/send',
    // ab_support: 声明前端支持模型 A/B 对比(response_type=10)，老逻辑不受影响
    data: { stream: true, ab_support: true, ...data },
  }, { onData })
}

// 模型 A/B 对比：选择更优的回复(a/b/tie)
export const abChoose = async (
  data: { ab_record_id: number, choice: AbChoice },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<{
    ab_record_id: number
    choice: AbChoice
    id: number
    tag: number
    content: string
    translate_content: string
  }>>({
    url: '/client/session/abChoose',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

// 模型 A/B 对比：提交选择理由(可选)
export const abReason = async (
  data: { ab_record_id: number, reasons?: string[], reason_text?: string },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<{ ab_record_id: number }>>({
    url: '/client/session/abReason',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

// 模型 A/B 对比 opt_out 取值：1=参与测试，2=不参与(退出)
export const AB_OPT_IN = 1
export const AB_OPT_OUT = 2
export type AbOptOutValue = typeof AB_OPT_IN | typeof AB_OPT_OUT 

// 模型 A/B 对比：读取当前参与状态(opt_out: 1=参与, 2=不参与)
export const getAbOptOut = async (
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<{ opt_out: AbOptOutValue }>>({
    url: '/client/session/abOptOut',
    method: 'get',
    ...option
  }, useFetch)
}

// 模型 A/B 对比：设置参与状态(opt_out: 1=参与, 2=不参与)
export const setAbOptOut = async (
  data: { opt_out: AbOptOutValue },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<{ opt_out: AbOptOutValue }>>({
    url: '/client/session/abOptOut',
    data,
    method: 'post',
    ...option
  }, useFetch)
}



// 获取活跃的聊天历史记录
export const getChatHistory = async (
  params?: {
    session_id: string,
    user_id: string,
    chat_id: string
    page_size: number,
    page: number
    script_chat_id?: string
  },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<{
    "id": number,
    "user_id": number,
    "session_id": number,
    "chat_id": string,
    "created_at": string,
    "updated_at": string,
    "deleted_at"?: string,
    "chat_record": ChatRecord[]
  }>>({
    url: '/client/session/history',
    params,
    method: 'get',
    ...option
  }, useFetch)
}


// 获取指定的chatId历史
export const getChatHistoryById = async (
  params?: {
    page_size: number,
    page: number
    id: number
  },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/story/history',
    params,
    method: 'get',
    ...option
  }, useFetch)
}


// 置顶一个消息
export const pinChat = async (
  data?: any,
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/pinned',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

// 设置模型
export const setModel = async (
  data?: { model_id: number, model_param: any[] },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/set-model',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

// 设置会话模型输出档位
export const setChatModelSelection = async (
  data?: { model_id: number, output_tier: string },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/set-chat-model-selection',
    data,
    method: 'post',
    ...option
  }, useFetch)
}


// 获取pined数据
export const getPinedList = async (
  params?: { session_id: number, chat_id: string, page: number, page_size: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/message/pin/list',
    params,
    ...option
  }, useFetch)
}

// 删除pined消息
// 参数放请求体(body)，按 body 签名，与 App 端 addSign1 一致、与后端 rawBody 校验对齐。
export const deletePinedMessage = async (
  data?: { session_id: number, chat_id: string, messages: { tag: number, id: number }[] },
  option?: ComplexRequestOption,
  useFetch = false) => {
  return newRequest<ResponseType<any>>({
    url: '/client/message/pin/del',
    data,
    method: "delete",
    ...option
  }, useFetch)
}



// 配置机器人
export const setChatConfig = async (
  data?: {
    session_id: number,
    shy?: number,
    model_id?: number,
    style?: number
    model_param?: any[]
    lorebook_id?: number
    lorebook_enable?: number
  },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/setting',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

/** 写会话情景图开关，只带 session_id + scene_images_open，勿走 setting */
export const setSessionSceneImagesOpen = async (
  data: { session_id: number; scene_images_open: 0 | 1 },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ session_id?: number }>>({
    url: '/client/session/extra',
    data,
    method: 'post',
    ...option,
  }, useFetch)
}

// 配置模型
export const getModels = async (
  params?: any,
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<ChatModelsPayload>>({
    url: '/client/config/models',
    params,
    ...option
  }, useFetch)
}

// 保存当前会话
export const saveChat = async (
  data?: {
    session_id: number,
  },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/story/add',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

// 获取历史会话列表
export const getChatHistoryList = async (
  params?: { session_id: string, page?: number, page_size?: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/story/list',
    params,
    ...option
  }, useFetch)
}

// 解锁一个图片
export const unlockImage = async (
  data?: {
    tag: number
    id: number
    chat_id: string
    session_id: number
    from_advert: boolean
  },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/message/picture',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 将当前记录设置为默认会话
export const editChat = async (
  data?: { session_id: number, chat_id: string },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/story/edit',
    method: 'post',
    data,
    ...option
  }, useFetch)
}



// 删除会话
// 参数放请求体(body)，按 body 签名，与 App 端 addSign1 一致、与后端 rawBody 校验对齐。
export const deleteChat = async (
  data: { session_ids: number[] },
  option?: ComplexRequestOption,
  useFetch = false) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/delete',
    method: 'delete',
    data,
    ...option
  }, useFetch)
}


// 删除会话历史
export const deleteChatHistory = async (
  data?: { session_id: number, chat_id: string },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/story/del',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 删除会话记录
// 参数放请求体(body)，按 body 签名，与 App 端 addSign1 一致、与后端 rawBody 校验对齐。
export const deleteChatRecord = async (
  data?: { session_id: number, chat_id: string, historys: any[] },
  option?: ComplexRequestOption,
  useFetch = false) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/history/delete',
    method: 'delete',
    data,
    ...option
  }, useFetch)
}

// 修改会话
export const modifyChatRecord = async (
  data?: { session_id: number, chat_id: string, historys?: ChatRecord[] },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/history/modify',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 刷新消息
export const refreshChatRecord = async (
  data: { id: number, tag: number },
  { onData }) => {
  return streamRequest({
    url: '/client/session/resend',
    data: { stream: true, ...data },
  }, { onData })
}

// 点赞 / 不喜欢。srore：1 喜欢，-1 不喜欢。
// 不喜欢时 reason 为具体原因：1 角色不匹配，2 冒犯性，3 过于笼统，4 无意义。
export const judgeChatRecord = async (
  data?: { session_id: number, chat_id: string, historys?: { id: number, reason: number, srore: number, tag: number }[] },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/history/judge',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

/** 不喜欢原因，与菜单顺序一致 */
export type DislikeReason = 1 | 2 | 3 | 4

/** 聊天举报 extra。后端按字段读取，没有的键不要传 */
export type ChatMessageReportExtra = {
  tag?: number
  image_url?: string
  content?: string
  translate_content?: string
  session_id?: number | string
  robot_id?: number
  /** 消息 type，不是外层固定的 "chat" */
  type?: number
  chat_id?: string
  content_type?: number
}

const compactReportExtra = (extra: ChatMessageReportExtra) => {
  return Object.fromEntries(
    Object.entries(extra).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ) as ChatMessageReportExtra
}

/** 举报一条聊天消息 POST /client/common/message/report。type 固定为 chat */
export const reportChatMessage = async (
  data: {
    id: number
    /** 举报类别，使用稳定英文枚举，与角色举报一致 */
    reason: string
    remark?: string
    extra?: ChatMessageReportExtra
  },
  option?: ComplexRequestOption,
  useFetch = true) => {
  const extra = compactReportExtra(data.extra ?? {})
  return newRequest<ResponseType<any>>({
    url: '/client/common/message/report',
    method: 'post',
    data: {
      type: "chat",
      id: data.id,
      reason: data.reason,
      ...(data.remark ? { remark: data.remark } : {}),
      ...(Object.keys(extra).length ? { extra } : {}),
    },
    ...option
  }, useFetch)
}

// 设置聊天人设
export const setSessionPersona = async (
  data?: { session_id: number, user_character_id: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/persona/set',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 获取(头像框和气泡)样式列表
export const getChatStyleList = async (
  params?: { type: number, page: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/chat/style',
    params,
    ...option
  }, useFetch)
}

// 解锁一个气泡
export const unlockChatStyleList = async (
  params?: { id: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/chat/style/unlock',
    params,
    ...option
  }, useFetch)
}

// 保存聊天样式
export const saveStyle = async (
  data?: { session_id: number, styles: any[] },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/chat/style/save',
    data,
    method: "post",
    ...option
  }, useFetch)
}

// 重置模型设置
export const resetModelParams = async (
  data?: { model_id: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/config/model/reset',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 播放机器人语言
export const speakMessage = async (
  data?: { id: number, tag: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/speak',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 获取灵感回复
export const getInspirationReply = async (
  data?: { chat_id: string, session_id: number, scene_script_id?: string },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/chat/idea',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// 设置会话别名
export const setSessionName = async (
  data?: { session_id: number, session_name: string },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/session/set-name',
    method: 'post',
    data,
    ...option
  }, useFetch)
}

// pin一条消息
export const pinMessage = async (
  data?: { session_id: number, chat_id: string, tag: number, id: number },
  option?: ComplexRequestOption,
  useFetch = true) => {
  return newRequest<ResponseType<any>>({
    url: '/client/message/pin/add',
    method: 'post',
    data,
    ...option
  }, useFetch)
}


// 模型 A/B 对比相关类型
export type AbChoice = 'a' | 'b' | 'tie'

export type AbCandidate = {
  content: string
  translate_content?: string
}

export type AbTestData = {
  ab_record_id: number
  candidates: {
    a: AbCandidate
    b: AbCandidate
  }
  // 前端左右展示顺序，盲化("ab" 或 "ba")
  display_order: string
  // 后端已默认落库的候选(a/b)
  default_choice: string
  // 用户最终选择(a/b/tie)，未选择时为空
  choice?: AbChoice
}

export type ChatRecordBilling = {
  billing_mode?: number
  asset_type?: number
  prompt_tokens?: number
  completion_tokens?: number
  cost?: number
  is_estimated?: number
}

/** 历史/发送 extra 里的实际扣费；cost 可能是 number 或 string */
export const getChatRecordBilling = (extra?: unknown) => {
  let parsed: { billing?: ChatRecordBilling } | null | undefined
  if (typeof extra === 'string') {
    try { parsed = JSON.parse(extra) } catch { return null }
  } else {
    parsed = extra as { billing?: ChatRecordBilling } | null | undefined
  }
  if (!parsed?.billing) return null
  const cost = Number(parsed.billing.cost)
  if (!Number.isFinite(cost)) return null
  return {
    cost,
    assetType: Number(parsed.billing.asset_type) || undefined,
  }
}

export type ChatRecord = Partial<{
  "id": number,
  "uuid": string,
  "session_id": number,
  "chat_id": string,
  "type": number,
  "src_id": number,
  "at_type": null,
  "at_ids": null,
  is_greet: boolean
  "content_type": number,
  "content": string,
  "lang_code": SupportLangsTypeList,
  "translate_content": string,
  "image_url": string,
  "extra": {
    "has-sensitive": boolean
    "tts-url"?: string
    "tts-task-id"?: string
    pin?: number
    billing?: ChatRecordBilling
  },
  "digg": number,
  "digg_reason": number,
  "send_time": string,
  "created_at": string,
  "updated_at": string,
  "server_timestamp": number,
  "time_difference": number,
  "send_timestamp": number,
  "tag": number,
  "user_id": number,
  "robot_id": number,
  "has_sensitive": boolean
  _contentCache?: string[]
  _contentCacheIndex?: number
  typing?: boolean // 是否正在打字
  ab_pending?: boolean // 模型 A/B 对比生成中(收到 ab_start 信号后、结果返回前，渲染双骨架)
  // 模型 A/B 对比流式增量(type=12)累积的候选内容，生成期间在双骨架内实时渲染，
  // type=10 完整结果到达后整体替换占位，该字段随之丢弃
  ab_stream?: Partial<Record<'a' | 'b', AbCandidate>>
  ab_test?: AbTestData // 模型 A/B 对比数据(命中采样时)
}>

// 清除消息上缓存的 TTS 语音信息。
// 当回复内容发生变化（重新生成 / 切换回复版本）后，旧音频已不再匹配，
// 需要清除缓存，以便下次播放时重新合成，避免播放到旧语音。
export const clearMessageTtsCache = <T extends Pick<ChatRecord, "extra">>(record: T) => {
  if (record.extra) {
    delete record.extra["tts-url"]
    delete record.extra["tts-task-id"]
  }
  return record
}

export type ChatMemoryRole = {
  id: number
  name: string
  /** 0 普通属性 1 机器人属性 2 用户属性 */
  type: number
}

export type ChatMemoryConfig = {
  /** 0 关闭 1 开启 */
  enable: number
  trial_expired_at: number
}

export type ChatMemoryRolesData = {
  roles: ChatMemoryRole[]
  /** null 表示该机器人不可用自动记忆 */
  memory_config: ChatMemoryConfig | null
  is_free_trial: boolean
  /** 0 未购买或已过期；>0 能量购买仍在有效期内 */
  expired_at: number
  /** 0|null 普通用户，1 VIP，2 超级会员 */
  vip_type: number | null
  upgrade?: {
    coin: number
    /** 购买后的有效时长，单位小时 */
    expired: number
  }
}

/**
 * 角色列表里的 expired_at / trial_expired_at 有两种写法：
 * 0 表示未购买或已过期；较小的正数是剩余时长；10 位/13 位或日期字符串是过期时间本身。
 * 购买接口返回的是过期时间戳，不能只判断 > 0 却把已过期的时间戳当成仍有效，也不能把日期字符串当成未购买。
 */
export function isChatMemoryExpiryActive(value: unknown, now = Date.now()) {
  if (value == null || value === "") return false
  if (typeof value === "string" && Number.isNaN(Number(value))) {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) && parsed > now
  }
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return false
  if (n >= 1e12) return n > now
  if (n >= 1e9) return n * 1000 > now
  return true
}

export function isChatMemoryUnlocked(info?: ChatMemoryRolesData | null) {
  if (!info) return false
  const vip = Number(info.vip_type)
  if (vip === 1 || vip === 2) return true
  if (info.is_free_trial) return true
  if (isChatMemoryExpiryActive(info.expired_at)) return true
  return isChatMemoryExpiryActive(info.memory_config?.trial_expired_at)
}

export type ChatMemoryAttribute = {
  id: number
  memory_role_id: number
  name: string
  name_key?: string
  value: string[]
  updated_at?: string
  created_at?: string
  default_lang?: string
}

// 获取记忆箱里可切换的角色，以及自动记忆的开关、试用和购买状态
export const getChatMemoryRoles = async (
  params: { chat_id: string, session_id: number },
  option?: ComplexRequestOption,
  useFetch = true
) => {
  return newRequest<ResponseType<ChatMemoryRolesData>>({
    url: '/client/chat/memory/roles',
    params,
    method: 'get',
    ...option
  }, useFetch)
}

// 按当前状态翻转自动记忆，不接收目标开关值
export const toggleChatMemory = async (
  data: { session_id: number },
  option?: ComplexRequestOption,
  useFetch = true
) => {
  return newRequest<ResponseType<ChatMemoryConfig & { id: number, session_id: number, extra?: unknown }>>({
    url: '/client/chat/memory/enable',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

export const buyChatMemory = async (
  data: { session_id: number },
  option?: ComplexRequestOption,
  useFetch = true
) => {
  return newRequest<ResponseType<{ expired_at: number }>>({
    url: '/client/chat/memory/buy',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

export const getChatMemoryDetail = async (
  params: { role_id: number },
  option?: ComplexRequestOption,
  useFetch = true
) => {
  return newRequest<ResponseType<ChatMemoryAttribute[]>>({
    url: '/client/chat/memory/detail',
    params,
    method: 'get',
    ...option
  }, useFetch)
}

export const deleteChatMemoryAttribute = async (
  data: { ids: number[], memory_role_id: number },
  option?: ComplexRequestOption,
  useFetch = true
) => {
  return newRequest<ResponseType<unknown>>({
    url: '/client/chat/memory/delete-attribute',
    data,
    method: 'post',
    ...option
  }, useFetch)
}

export const updateChatMemoryAttribute = async (
  data: { id: number, values: string[] },
  option?: ComplexRequestOption,
  useFetch = true
) => {
  return newRequest<ResponseType<unknown>>({
    url: '/client/chat/memory/update-attribute',
    data,
    method: 'post',
    ...option
  }, useFetch)
}